<template>
  <v-simple-table fixed-header>
    <template v-slot:default>
      <thead>
        <tr>
          <th class="text-center" style="width: 80px">記事ID</th>
          <th style="min-width: 400px" />
          <th style="min-width: 150px">ライター</th>
          <th style="width: 150px" />
          <th style="width: 80px" />
          <th style="width: 80px" />
          <th v-if="publishOrDeletePermission()" style="width: 50px" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="post in posts" :key="post.id">
          <td class="text-center">
            {{ post.id }}
          </td>

          <td class="py-2">
            <template
              v-if="
                post.recentOrder && !writerCompleted(post.recentOrder.status)
              "
            >
              <v-chip
                class="ma-2 font-weight-bold"
                x-small
                label
                :color="orderStatusColors[post.recentOrder.status]"
              >
                {{ i18nOrderStatus(post.recentOrder) }}
              </v-chip>
            </template>
            <template v-else>
              <v-chip
                class="ma-2 font-weight-bold"
                :color="postStatusColors[post.advancedStatus]"
                x-small
                label
              >
                {{ postI18ns.status[post.advancedStatus] }}
              </v-chip>
            </template>
            <v-icon v-if="post.postType === 'product'">mdi-cart</v-icon>
            <v-chip v-if="post.category" x-small outlined>
              {{ post.category.name }}
            </v-chip>
            <div class="text-subtitle-1 pa-1">
              {{ post.title }}
            </div>

            <div class="ml-2 my-2 caption"></div>
          </td>
          <td>
            <div v-if="writer(post)" class="d-flex justify-start align-center">
              <v-avatar size="30" :color="writerColor(writer(post))">
                <img
                  v-if="writer(post).avatar.thumb.url"
                  :src="writer(post).avatar.thumb.url"
                />
                <span v-else class="white--text caption">{{
                  writer(post).name ? writer(post).name.slice(0, 1) : ''
                }}</span>
              </v-avatar>
              <span v-if="writer(post).name" class="ml-2 caption">
                {{ writer(post).name }}
              </span>
              <span v-else class="ml-2 caption font-weight-light font-italic">
                未設定
              </span>
            </div>
          </td>
          <td>
            <template v-if="post.status === 'published'">
              <v-chip color="success" label x-small outlined> 公開日 </v-chip>
              <div class="ml-1">{{ post.releasedOn | momentDate }}</div>
            </template>
            <template v-else-if="post.pendingPublishReservation">
              <v-chip color="primary" label x-small outlined> 予約日時 </v-chip>
              <div class="ml-1">
                {{ post.pendingPublishReservation.reservedAt | momentDatetime }}
              </div>
            </template>
            <template v-else>
              <v-chip x-small label> 最終更新 </v-chip>
              <div class="ml-1">{{ post.updatedAt | momentDatetime }}</div>
            </template>
          </td>
          <td class="text-center px-1">
            <v-btn
              class="editLink ma-2"
              :href="postEditorUrl(post)"
              target="_blank"
              tile
              small
              color="primary"
            >
              <v-icon left small> mdi-pencil </v-icon>
              編集
            </v-btn>
          </td>
          <td class="text-center px-0">
            <v-btn
              v-if="post.status === 'published'"
              :href="postPublicUrl(post)"
              target="_blank"
              x-small
              color="success"
              class="mb-2"
              outlined
            >
              公開画面
            </v-btn>

            <v-btn
              :href="postPreviewUrl(post)"
              target="_blank"
              x-small
              outlined
            >
              プレビュー
            </v-btn>
          </td>
          <td v-if="publishOrDeletePermission()" class="text-center px-0">
            <v-menu bottom right>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-card tile flat>
                <v-list nav dense>
                  <v-list-item
                    v-if="publishPermission()"
                    @click="postDlUrl(post.id)"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon>mdi-download</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>HTMLダウンロード</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    v-if="publishPermission()"
                    @click="postDuplicate(post.id)"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon> mdi-content-copy </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>複製する</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    v-if="deletePermission()"
                    @click="deletePost(post.id)"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon color="error">mdi-delete-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="error--text">
                      <v-list-item-title>削除する</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </td>
        </tr>
      </tbody>
    </template>
  </v-simple-table>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import orderUtils from '@/mixins/order_utils'
import snakecaseKeys from 'snakecase-keys'
import urlUtils from '@/mixins/url_utils'

export default {
  filters: {
    momentDatetime: function (val) {
      if (val) {
        return moment(val).format('YYYY/M/D HH:mm')
      }
    },
    momentDate: function (val) {
      if (val) {
        return moment(val).format('YYYY/M/D')
      }
    }
  },
  mixins: [orderUtils, urlUtils],
  props: {
    posts: {
      type: Array,
      required: true
    },
    site: {
      type: Object,
      required: false,
      default: () => {}
    }
  },
  data: () => {
    return {
      orderStatusColors: {
        notApplied: '',
        writing: 'primary',
        underDetection: 'warning',
        underInspection: 'warning',
        underClientInspection: 'warning',
        completed: 'success'
      },
      postStatusColors: {
        draft: 'warning',
        published: 'success',
        closed: 'error',
        reserved: 'primary'
      }
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.UserStore.currentUser,
      currentUserAuth: (state) => state.UserAuthStore.currentUserAuth,
      orderI18ns: (state) => snakecaseKeys(state.I18n.enums.order),
      postI18ns: (state) => state.I18n.enums.post
    })
  },
  methods: {
    referUserName (order) {
      if (!order.user) {
        return null
      } else {
        return order.user.name
      }
    },
    referCategory (postTags) {
      const category = postTags.find((el) => el['isTopLevel'] === true)
      if (!category || !category.name) {
        return null
      } else {
        return category.name
      }
    },
    publishPermission () {
      if (this.currentUserAuth.id) {
        return true
      }

      if (
        this.currentUser.id &&
        this.currentUser.permittedSituations.includes('post_publication')
      ) {
        return true
      }

      return false
    },
    deletePermission () {
      if (this.currentUserAuth.id) {
        return true
      }

      if (
        this.currentUser.id &&
        this.currentUser.permittedSituations.includes('post_deletion')
      ) {
        return true
      }

      return false
    },
    publishOrDeletePermission () {
      return this.publishPermission() || this.deletePermission()
    },
    postDlUrl (postId) {
      this.$emit('post-download-url', postId)
    },
    postEditorUrl (post) {
      return this.selectedSiteUrlOrigin() + '/admin/posts/' + post.id + '/edit'
    },
    postPreviewUrl (post) {
      return this.selectedSiteUrlOrigin() + '/preview/' + post.id
    },
    postPublicUrl (post) {
      return this.selectedSiteUrlOrigin() + this.mediaRootPath() + post.slug
    },
    selectedSiteUrlOrigin () {
      if (!this.site || !this.site.id) {
        return ''
      }

      return this.site.initialDomainWithProtocol
    },
    writer (post) {
      if (post.writerable) {
        return post.writerable
      }
      if (post.recentOrder && post.recentOrder.user) {
        return post.recentOrder.user
      }
      return null
    },
    writerColor (writer) {
      if (writer.avatar.thumb.url) {
        return null
      }
      const colors = ['indigo', 'teal', 'red']
      return colors[writer.id % 3]
    },
    postDuplicate (postId) {
      this.$emit('post-duplicate', postId)
    },
    deletePost (postId) {
      if (confirm('削除した記事は二度と復元できません。本当に削除しますか？')) {
        this.$emit('delete-post', postId)
      }
    },
    i18nOrderStatus (order) {
      return snakecaseKeys(this.orderI18ns.status, {'deep': true})[order.status]
    }
  }

}
</script>

<style scoped lang="sass">
th, td
  vertical-align: middle
</style>
